import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Radio, ShieldCheck, UserRoundCheck } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function YasharalLivePresenterInvite() {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Checking your presenter invitation…");

  const inviteData = useMemo(() => {
    const raw = (location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(raw);
    return {
      inviteCode: params.get("code") || "",
      roomKey: params.get("key") || "",
    };
  }, [location.hash]);

  const nextPath = `/yasharal-live/presenter/${roomId}${location.hash || ""}`;
  const loginTarget = `/yasharal-live/login?next=${encodeURIComponent(nextPath)}`;
  const signupTarget = `/yasharal-live/signup?next=${encodeURIComponent(nextPath)}`;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!inviteData.inviteCode) {
        setStatus("error");
        setMessage("This presenter invitation is missing its secure invite code.");
        return;
      }
      try {
        await axios.get(`${API}/yasharal-live/auth/me`);
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 401) {
          setStatus("auth");
          setMessage("Presenters must sign in or create a Yasharal Live account before Studio access is granted.");
          return;
        }
        setStatus("error");
        setMessage(error?.response?.data?.detail || "Could not verify your Yasharal Live account.");
        return;
      }

      try {
        await axios.post(`${API}/communications/rooms/${roomId}/presenter-invite/accept`, {
          invite_code: inviteData.inviteCode,
        });
        if (cancelled) return;
        setStatus("accepted");
        setMessage("Presenter access granted.");
        const secureHash = inviteData.roomKey ? `#key=${encodeURIComponent(inviteData.roomKey)}` : "";
        setTimeout(() => navigate(`/yasharal-live/rooms/${roomId}${secureHash}`, { replace: true }), 650);
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status === 401) {
          setStatus("auth");
          setMessage("Sign in or create an account to accept this presenter invitation.");
          return;
        }
        setStatus("error");
        setMessage(error?.response?.data?.detail || "This presenter invitation could not be accepted.");
      }
    };
    run();
    return () => { cancelled = true; };
  }, [inviteData.inviteCode, inviteData.roomKey, navigate, roomId]);

  return (
    <div className="min-h-screen bg-[#070a10] text-white flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-[#111318] p-7 shadow-2xl">
        <div className="h-14 w-14 rounded-2xl bg-[#183c73] flex items-center justify-center mb-5">
          {status === "accepted" ? <UserRoundCheck className="w-7 h-7 text-emerald-300" /> : <Radio className="w-7 h-7 text-[#9dc1ff]" />}
        </div>
        <h1 className="text-2xl font-semibold">Yasharal Live presenter invitation</h1>
        <p className="mt-3 text-slate-300 leading-relaxed">{message}</p>

        {status === "auth" && (
          <div className="mt-6 space-y-3">
            <div className="rounded-xl border border-[#8bb6ff]/30 bg-[#183c73]/20 p-4 text-sm text-slate-300 flex gap-3">
              <ShieldCheck className="w-5 h-5 text-[#9dc1ff] flex-shrink-0 mt-0.5" />
              <span>Meeting-only guests can join without an account. Studio Presenters are authenticated separately.</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Button asChild className="bg-[#8bb6ff] text-slate-950"><Link to={loginTarget}>Sign in</Link></Button>
              <Button asChild variant="outline" className="border-slate-600 text-white"><Link to={signupTarget}>Create account</Link></Button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="mt-6"><Button variant="outline" className="border-slate-600 text-white" onClick={() => navigate("/yasharal-live")}>Return to Yasharal Live</Button></div>
        )}
      </div>
    </div>
  );
}
